import { Link } from 'react-router-dom';

import { DataTable, type Column } from '../components/DataTable.tsx';
import { Pagination } from '../components/Pagination.tsx';
import { dateOnly, money, statusClass, statusLabel } from '../lib/format.ts';
import { usePaginatedList } from '../lib/usePaginatedList.ts';

interface SalesReturnRow {
  id: number;
  srNo: string;
  srDate: string;
  customerNo: string | null;
  currency: string;
  value: number;
  status: string;
  ref: string | null;
}

export function SalesReturnsListPage() {
  const list = usePaginatedList<SalesReturnRow>({
    endpoint: '/api/dashboard/sales-returns',
    defaultSortColumn: 'sr_date',
    defaultSortDirection: 'DESC',
  });

  const columns: Column<SalesReturnRow>[] = [
    {
      key: 'sr_no',
      label: 'SR No',
      sortable: true,
      className: 'mono',
      render: (r) => (
        <Link to={`/sales-returns/${encodeURIComponent(r.srNo)}`}>{r.srNo}</Link>
      ),
    },
    {
      key: 'sr_date',
      label: 'Date',
      sortable: true,
      className: 'mono',
      render: (r) => dateOnly(r.srDate),
    },
    {
      key: 'customer_no',
      label: 'Customer',
      sortable: true,
      render: (r) =>
        r.customerNo ? (
          <Link to={`/customers/${encodeURIComponent(r.customerNo)}`}>{r.customerNo}</Link>
        ) : (
          '—'
        ),
    },
    {
      key: 'value',
      label: 'Value',
      sortable: true,
      className: 'num',
      render: (r) => money(r.value, r.currency),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (r) => <span className={`badge ${statusClass(r.status)}`}>{statusLabel(r.status)}</span>,
    },
    { key: 'ref', label: 'Ref', className: 'mono', render: (r) => r.ref ?? '—' },
  ];

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Sales returns</h1>
        <span className="main-subtitle">{list.data ? `${list.data.total} total` : ''}</span>
      </header>

      <div className="toolbar">
        <input
          placeholder="Search SR no or ref…"
          defaultValue={list.state.search}
          onChange={(e) => list.setSearch(e.target.value)}
        />
      </div>

      {list.error && <div className="error">{(list.error as Error).message}</div>}
      {list.isLoading && !list.data && <div className="loading">Loading…</div>}

      {list.data && (
        <>
          <DataTable
            columns={columns}
            rows={list.data.rows}
            rowKey={(r) => r.id}
            sortColumn={list.state.sortColumn}
            sortDirection={list.state.sortDirection}
            onSortChange={list.toggleSort}
          />
          <Pagination
            page={list.state.page}
            pageSize={list.state.pageSize}
            total={list.data.total}
            onPageChange={list.setPage}
          />
        </>
      )}
    </>
  );
}
